import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import {
  RECEIPT_TOTALS_QUERY,
  type ReceiptTotalsGraphQLResponse,
} from "./useReceiptsGraphQL";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

/**
 * Shape returned by GET /api/v1/promises (we only need the pagination total)
 */
interface PromisesApiResponse {
  pagination: { totalItems: number };
}

/**
 * Shape returned by GET /api/v1/collection-cases (we only need the pagination total)
 */
interface CollectionCasesApiResponse {
  pagination: { totalItems: number };
}

/** Reusable type alias for API query parameter values */
type ApiParamValue = string | number | boolean;

export interface TodaysProgressData {
  paymentsAllocated: { count: number; totalAmount: number };
  promisesKept: { count: number };
  casesResolved: { count: number };
}

/** Format a Date as YYYY-MM-DD for API query params */
function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Hook to fetch "Today's Progress" metrics for the dashboard progress bar.
 *
 * In **live** mode it fires parallel API calls:
 *   1. GraphQL receiptTotals – fully allocated today → count + total £
 *   2. GET /api/v1/promises   – fulfilled (Paid) today → count
 *   3. GET /api/v1/collection-cases – closed, optionally by user → count
 *
 * In **demo** mode it derives the same numbers from the global mock-data store.
 *
 * @param selectedUserId  Pass a user ID string when a specific user is selected,
 *                        or `undefined` for "Everyone".
 */
export function useTodaysProgress(selectedUserId?: string) {
  const { apiGet } = useApi();
  const apiConfig = useApiConfig();

  // ── Demo data ──────────────────────────────────────────────────────
  const store = getGlobalDataStore();

  const paymentsReceivedToday = store.getReceiptsReceivedToday();
  const promisesKeptToday = store.getPromisesKeptToday();
  const casesResolvedToday = store.getCasesResolvedToday();

  const demoData: TodaysProgressData = {
    paymentsAllocated: {
      count: paymentsReceivedToday.length,
      totalAmount: paymentsReceivedToday.reduce(
        (sum, r) => sum + r.receivedAmount,
        0,
      ),
    },
    promisesKept: {
      count: promisesKeptToday.length,
    },
    casesResolved: {
      count: casesResolvedToday.length,
    },
  };

  // ── Live fetcher ───────────────────────────────────────────────────
  const today = toDateString(new Date());

  return useModeAwareQuery<TodaysProgressData, Error>(
    ["todays-progress", selectedUserId ?? "everyone"],
    async ({ signal }) => {
      // Build GraphQL filter for receipt totals
      const receiptFilter: Record<string, unknown> = {
        transactionDate: { ge: today, le: today },
      };
      if (selectedUserId) {
        receiptFilter.creditControllerId = { eq: Number(selectedUserId) };
      }

      const promisesParams: Record<string, ApiParamValue> = {
        Status: "Paid",
        FulfilledDateFrom: today,
        FulfilledDateTo: today,
        Page: 1,
        PageSize: 1,
        SortBy: "PromiseDate",
        SortOrder: "desc",
      };
      if (selectedUserId) {
        promisesParams.AssignedUserId = Number(selectedUserId);
      }

      const casesParams: Record<string, ApiParamValue> = {
        Status: "closed",
        Page: 1,
        PageSize: 1,
        SortBy: "CreatedAt",
        SortOrder: "desc",
      };
      if (selectedUserId) {
        casesParams.AssignedTo = Number(selectedUserId);
      }

      // Fire all three requests in parallel
      const [receiptTotalsResponse, promisesResponse, casesResponse] =
        await Promise.all([
          graphqlQuery<ReceiptTotalsGraphQLResponse>(
            apiConfig,
            RECEIPT_TOTALS_QUERY,
            { filter: receiptFilter },
            signal,
          ),
          apiGet<PromisesApiResponse>("/api/v1/promises", promisesParams, signal),
          apiGet<CollectionCasesApiResponse>("/api/v1/collection-cases", casesParams, signal),
        ]);

      const totals = receiptTotalsResponse.receipts.receiptTotals;

      return {
        paymentsAllocated: {
          count: totals.fullyAllocatedCount,
          totalAmount: totals.fullyAllocatedAmount,
        },
        promisesKept: {
          count: promisesResponse.pagination.totalItems,
        },
        casesResolved: {
          count: casesResponse.pagination.totalItems,
        },
      };
    },
    demoData,
    { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false },
  );
}
